import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Table, Tag, Tooltip, Radio, Modal } from 'antd';
import { toast, Bounce } from 'react-toastify';
import { Button } from 'components/Button';
import FilterSelect from 'components/Form/filterSelect';
import {
  getPositionMappingsApi,
  bulkUpsertPositionMappingsApi,
  deletePositionMappingApi,
  getUniformItemsApi,
  uniformErrorMessage,
} from 'api/services/uniformManagement';
import { useSelector, useDispatch } from 'react-redux';
import { fetchPosition } from 'store/slice/common';
import DeleteModal from 'components/modelpopup/DeleteModal';
import PageHeader from 'components/PageHeader';
import MasterDataRetryAlert from '../../components/MasterDataRetryAlert';
import useStickyIdentity from '../../components/useStickyIdentity';
import { itemRender, pageSizeOptions } from 'utils/table';

const toastOpts = { position: 'top-right', autoClose: 3000, transition: Bounce };

/** Above this many mappings in one action, confirm before writing. Deliberately
 *  looser than the issuance-side modal's threshold of 15: many-to-many is the
 *  *point* of this screen, so confirming a routine "6 items x 4 positions" would
 *  just train the user to dismiss the dialog. 50 is where a selection stops
 *  looking deliberate and starts looking like a mis-click on "select all". */
const BULK_CONFIRM_THRESHOLD = 50;

/** Hard ceiling the endpoint enforces on one request's cross-product
 *  (`UNIFORM_POSITION_BULK_MAX_PAIRS` in uniform/serializers/masters.py). Mirrored
 *  here so a "select all x select all" on a large tenant is caught at the moment
 *  the selection crosses it, rather than after the user has read and confirmed a
 *  scale dialog for an assignment the server was always going to reject. */
const MAX_BULK_PAIRS = 5000;

/** Tags shown inline on a group row before collapsing the rest behind "+n". */
const INLINE_TAG_LIMIT = 6;

/** Selected chips drawn in an assignment picker before the rest collapse behind
 *  "+n more". "Select all" makes a several-hundred-chip control a routine state
 *  rather than a pathological one, and rendering every chip makes each keystroke
 *  in the search box re-lay-out the whole row. The cap is presentation only — the
 *  selection, the preview count and the payload are all unaffected. */
const PICKER_MAX_CHIPS = 12;

/** What the assignment should do to `rotation_cycle_months_override`.
 *
 *  The bulk endpoint treats the *presence* of that key as meaningful: omit it
 *  and existing rows keep whatever override they already carry (counted back as
 *  `unchanged`); send it — an explicit null included — and the value is written
 *  over every pair in the cross-product. So "leave alone" and "clear it" are two
 *  genuinely different intentions and cannot share one blank input. Three
 *  explicit modes, defaulting to the non-destructive one, is the only honest
 *  way to model that: an empty box must never be able to erase configuration.  */
const OVERRIDE_KEEP = 'keep';
const OVERRIDE_SET = 'set';
const OVERRIDE_CLEAR = 'clear';

const pluralise = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

/** A mapping is identified by its (uniform item, position) pair — the same
 *  unique constraint the backend enforces. Used to diff the pending selection
 *  against what already exists so the preview can be truthful. */
const pairKey = (itemId, positionId) => `${itemId}:${positionId}`;

/** The list serializer nests the item (`uniform_item: {...}`) but returns the
 *  position as a bare id plus `position_name`. Read the item id defensively so
 *  a flattened payload still resolves. */
const rowItemId = (row) => row.uniform_item?.id ?? row.uniform_item;

const StatPill = ({ tone, children }) => {
  // Both tones clear 4.5:1 against their own tint over the #FAFAFA strip, which
  // 12px/600 text needs (it is nowhere near the large-text exemption). The green
  // is #166534, not the theme's #16A34A: that lighter green measures ~2.9:1 on a
  // 10% tint of itself. The brand red measures ~4.6:1 and is unchanged.
  const tones = {
    new: { background: 'rgba(22,163,74,0.10)', color: '#166534', border: '1px solid rgba(22,101,52,0.22)' },
    existing: { background: 'rgba(211,0,26,0.08)', color: '#D3001A', border: '1px solid rgba(211,0,26,0.18)' },
  };
  return (
    <span
      style={{
        ...tones[tone],
        borderRadius: 999,
        padding: '3px 12px',
        fontSize: 12,
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
};

/** Label row for one side of the assignment: the field label and a live count.
 *  Rendered twice, so it lives here instead of being duplicated.
 *
 *  It used to carry a "Select all" button too. That button has been replaced by
 *  `FilterSelect`'s built-in `selectAll` row, which is strictly better for the
 *  reason the old comment here admitted: a control outside the menu cannot see
 *  the search text typed inside it, so it could only ever mean "all 412", never
 *  "all 9 matching 'apron'". The built-in row sits inside the menu and acts on
 *  exactly what the search leaves visible.
 *
 *  The count stays. It is not decoration and it is not something the in-menu row
 *  duplicates: the row is only visible while the menu is OPEN, and "0 of 412
 *  selected" is what tells the user the scale of the list before they open it,
 *  and what confirms the result after they close it.
 *
 *  There is no per-picker "Clear": react-select's own clear affordance (isClearable)
 *  already empties one picker, and the Clear button on the preview strip resets
 *  the whole assignment including the override mode. A third control would just
 *  duplicate one of those. */
const PickerHeader = ({ inputId, label, selectedCount, totalCount, loading }) => (
  <div className="d-flex justify-content-between align-items-baseline flex-wrap gap-2">
    <label className="col-form-label pt-0 pb-1 mb-0" htmlFor={inputId}>
      {label} <span className="text-danger">*</span>
    </label>
    {/* `aria-live="polite"` so ticking options is announced without
        interrupting the option the screen reader is currently reading, and
        `aria-atomic` so the whole phrase is re-read rather than just the
        digit that changed ("412" on its own means nothing). The region is
        always in the DOM — swapping the element in and out on `loading`
        would make some screen readers miss the first update after the
        fetch lands. */}
    <span className="text-muted small" aria-live="polite" aria-atomic="true">
      {/* During the initial fetch both counts are 0, and "0 of 0 selected"
          reads as a definitive empty list rather than "not loaded yet". */}
      {loading ? `Loading ${label.toLowerCase()}…` : `${selectedCount} of ${totalCount} selected`}
    </span>
  </div>
);

/** Turns the bulk endpoint's `{created, updated, unchanged, total}` into a
 *  sentence that names every non-zero bucket. Returns null when the counts are
 *  missing or non-numeric, so the caller falls back to a generic message rather
 *  than inventing numbers the server never sent.
 *
 *  The displayed total is the *sum* of the three buckets, not the reported
 *  `total`: if the two ever disagree, showing a total that contradicts its own
 *  breakdown is worse than quietly trusting the parts. */
const describeSaveResult = (result) => {
  const created = Number(result?.created);
  const updated = Number(result?.updated);
  const unchanged = Number(result?.unchanged);
  if (![created, updated, unchanged].every((n) => Number.isFinite(n))) return null;

  const total = created + updated + unchanged;
  if (total === 0) return { kind: 'info', text: 'Nothing to assign — no mappings were written.' };
  if (created === 0 && updated === 0) {
    return { kind: 'info', text: `No changes — ${pluralise(unchanged, 'mapping')} already up to date.` };
  }

  const parts = [];
  if (created) parts.push(`${created} created`);
  if (updated) parts.push(`${updated} updated`);
  if (unchanged) parts.push(`${unchanged} already up to date`);
  return { kind: 'success', text: `${pluralise(total, 'mapping')}: ${parts.join(', ')}.` };
};

const PositionMappingList = () => {
  const { position: positions, positionLoading, positionError } = useSelector((state) => state.common);
  const dispatch = useDispatch();
  // Hook form, not static Modal.confirm: the static call renders outside the
  // React tree and so never sees the app's ConfigProvider theme (index.jsx),
  // rendering unthemed.
  const [modal, modalContextHolder] = Modal.useModal();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  // Bumped after any write so the single fetch effect below re-runs. Refetching
  // through the effect (rather than calling a fetch function imperatively)
  // keeps every request under one `ignore` guard, and React tears down the
  // previous effect before starting the next — so a slow earlier response can
  // never overwrite a newer one, or reset `loading` out from under it.
  const [reloadToken, setReloadToken] = useState(0);
  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  const [uniformItems, setUniformItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);

  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedPositions, setSelectedPositions] = useState([]);
  const [overrideMode, setOverrideMode] = useState(OVERRIDE_KEEP);
  const [rotationOverride, setRotationOverride] = useState('');
  const [saving, setSaving] = useState(false);
  // Set when the endpoint answers 409 — a lost write race, not bad input. Held
  // in state rather than left to the toast alone because the toast disappears
  // in seconds and the useful instruction ("press Assign again") needs to
  // outlive it, next to the button it refers to.
  const [conflict, setConflict] = useState(false);

  const [groupBy, setGroupBy] = useState('position');
  // Multi-selects holding arrays of options. Purely client-side: these narrow
  // the already-loaded mapping list, so there is no query param and no fetch
  // to guard — which is also why they have no Apply button, unlike the
  // server-backed filters elsewhere in the module.
  const [filterItem, setFilterItem] = useState([]);
  const [filterPosition, setFilterPosition] = useState([]);
  // Expansion is driven from state rather than left to antd's internal store so
  // "Expand all"/"Collapse all" and the per-row chevrons write to one source of
  // truth. antd keeps the chevrons working as long as `onExpandedRowsChange`
  // feeds this back.
  const [expandedKeys, setExpandedKeys] = useState([]);

  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const positionOptions = useMemo(
    () => (positions || [])
      .map((p) => ({ label: p.name, value: p.id }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })),
    [positions]
  );

  // Every mapping is loaded once, unfiltered, and the browse filters below run
  // client-side. Two reasons: the endpoint is unpaginated anyway (it returns
  // `data: [...]` whole), and — more importantly — the pre-commit preview diffs
  // the pending selection against this list. If the list were narrowed by the
  // browse filters, the preview would report pairs as "new" that already exist
  // outside the current filter, which is exactly the lie upsert is meant to
  // remove.
  useEffect(() => {
    let ignore = false;
    setLoading(true);
    getPositionMappingsApi().then((response) => {
      if (ignore) return;
      setData(response.data?.data || []);
    }).catch((e) => {
      if (ignore) return;
      setData([]);
      toast.error(uniformErrorMessage(e, 'Failed to load position mappings'), toastOpts);
    }).finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, [reloadToken]);

  useEffect(() => {
    let ignore = false;
    setItemsLoading(true);
    getUniformItemsApi({ page_size: 1000 }).then((response) => {
      if (ignore) return;
      // Only active items can actually be issued (IssuanceEmployeesView filters
      // mapped items on status), so offering an inactive one here would create a
      // mapping that never reaches an employee. Existing mappings to a
      // since-deactivated item still show in the list below — flagged "Inactive"
      // under either grouping — so they remain visible and deletable.
      const options = (response.data?.results || [])
        .filter((i) => i.status !== false)
        .map((i) => ({ label: i.name, value: i.id }))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
      setUniformItems(options);
    }).catch((e) => {
      if (ignore) return;
      setUniformItems([]);
      toast.error(uniformErrorMessage(e, 'Failed to load uniform items'), toastOpts);
    }).finally(() => { if (!ignore) setItemsLoading(false); });
    return () => { ignore = true; };
  }, []);

  const existingByPair = useMemo(() => {
    const map = new Map();
    data.forEach((row) => {
      const itemId = rowItemId(row);
      if (itemId != null && row.position != null) map.set(pairKey(itemId, row.position), row);
    });
    return map;
  }, [data]);

  /** The cross-product the user is about to commit, diffed against what already
   *  exists. `existing` is deliberately labelled "already mapped" rather than
   *  "will be updated": only the server knows whether a re-submitted pair
   *  actually changes, and promising an update we can't guarantee would be the
   *  same dishonesty as a generic "Saved". */
  const preview = useMemo(() => {
    const total = selectedItems.length * selectedPositions.length;
    // The override each existing pair currently carries (null = none). Kept as
    // values rather than a count so the warning below can compare against what
    // the user is actually about to write, not merely notice that overrides exist.
    const existingOverrides = [];
    selectedItems.forEach((item) => {
      selectedPositions.forEach((pos) => {
        const row = existingByPair.get(pairKey(item.value, pos.value));
        if (!row) return;
        existingOverrides.push(row.rotation_cycle_months_override ?? null);
      });
    });
    const existing = existingOverrides.length;
    return { total, existing, fresh: total - existing, existingOverrides };
  }, [selectedItems, selectedPositions, existingByPair]);

  // Integer, not merely finite: the field is an IntegerField with min_value=1
  // server-side, so 1.5 and 0 both 400. Rejecting them here keeps the client
  // rule aligned with the backend instead of round-tripping a guaranteed error.
  const overrideValue = rotationOverride === '' ? null : Number(rotationOverride);
  const overrideInvalid =
    overrideMode === OVERRIDE_SET && !(Number.isInteger(overrideValue) && overrideValue >= 1);
  const overLimit = preview.total > MAX_BULK_PAIRS;
  const canAssign = preview.total > 0 && !overLimit && !overrideInvalid && !saving;

  /** How many EXISTING pairs would actually have their rotation changed. Only
   *  non-zero when the user has explicitly set or cleared the override, because
   *  those are the only modes that send the key at all — omitting it cannot
   *  touch an existing row, so warning about it would be false and would
   *  discourage what is now a completely safe action. */
  const overrideChangeCount = useMemo(() => {
    if (overrideMode === OVERRIDE_KEEP) return 0;
    if (overrideMode === OVERRIDE_SET && !Number.isInteger(overrideValue)) return 0;
    const target = overrideMode === OVERRIDE_SET ? overrideValue : null;
    return preview.existingOverrides.filter((current) => current !== target).length;
  }, [overrideMode, overrideValue, preview.existingOverrides]);

  // The 409 notice describes one specific payload. Making it persistent was
  // right — a toast expires long before the user can act on it — but persistence
  // has to end when the thing it describes stops being true. Any edit to the
  // selection or the override produces a different payload, at which point
  // "nothing was written, press Assign to try again" is about an assignment the
  // user is no longer making. Done as one effect over the whole payload rather
  // than a setConflict(false) in each of the four change handlers: this cannot
  // miss a path, including any added later. It cannot race the catch that sets
  // the flag either, since that catch mutates none of these.
  useEffect(() => {
    setConflict(false);
  }, [selectedItems, selectedPositions, overrideMode, rotationOverride]);

  const performSave = () => {
    setSaving(true);
    setConflict(false);
    const payload = {
      uniform_items: selectedItems.map((i) => i.value),
      positions: selectedPositions.map((p) => p.value),
    };
    // The key is sent ONLY when the user actively chose to set or clear it.
    // Under OVERRIDE_KEEP it is omitted entirely — sending an incidental null
    // would wipe every hand-tuned override in the cross-product.
    if (overrideMode === OVERRIDE_SET) payload.rotation_cycle_months_override = overrideValue;
    if (overrideMode === OVERRIDE_CLEAR) payload.rotation_cycle_months_override = null;
    bulkUpsertPositionMappingsApi(payload).then((response) => {
      const described = describeSaveResult(response.data?.data);
      if (!described) {
        toast.success('Assignment saved', toastOpts);
      } else if (described.kind === 'info') {
        toast.info(described.text, toastOpts);
      } else {
        toast.success(described.text, toastOpts);
      }
      // The items stay selected and only the positions clear: defining a kit
      // once and applying it to several positions in turn is the dominant
      // workflow here, and it also stops the button sitting enabled over what
      // is now a guaranteed no-op.
      setSelectedPositions([]);
      reload();
    }).catch((e) => {
      // 409 is not a validation failure and must not be dressed as one: the
      // selection was fine, another overlapping bulk write simply kept winning
      // the race through all three of the endpoint's savepoint retries. The
      // path stays atomic, so nothing at all was written — worth saying plainly
      // to someone who just tried to write a few hundred mappings. Deliberately
      // NOT routed through uniformErrorMessage: the server's own text reads as
      // an error, and repeating it here would push a retryable, blameless
      // outcome into the same register as "you sent something invalid". Every
      // other status still goes through the shared helper.
      if (e?.response?.status === 409) {
        setConflict(true);
        toast.warning(
          'Nothing was written — another assignment was saving at the same time. '
          + 'Your selection is unchanged, so press Assign to try again.',
          { ...toastOpts, autoClose: 6000 }
        );
        return;
      }
      toast.error(uniformErrorMessage(e, 'Failed to assign uniform mappings'), toastOpts);
    }).finally(() => setSaving(false));
  };

  const handleAssign = () => {
    if (!canAssign) return;
    if (preview.total <= BULK_CONFIRM_THRESHOLD) {
      performSave();
      return;
    }
    modal.confirm({
      title: `Assign ${pluralise(preview.total, 'mapping')}?`,
      centered: true,
      content: (
        <div>
          <div>
            {pluralise(selectedItems.length, 'uniform item')} across{' '}
            {pluralise(selectedPositions.length, 'position')} — {preview.fresh} new,{' '}
            {preview.existing} already mapped.
          </div>
          {overrideChangeCount > 0 && (
            <div className="mt-2">
              {overrideMode === OVERRIDE_SET
                ? `${pluralise(overrideChangeCount, 'existing mapping')} will change to a rotation of ${overrideValue} months.`
                : `${pluralise(overrideChangeCount, 'existing mapping')} will lose the rotation override and fall back to the uniform item default.`}
            </div>
          )}
        </div>
      ),
      okText: 'Assign',
      cancelText: 'Cancel',
      onOk: performSave,
    });
  };

  const handleClearSelection = () => {
    setSelectedItems([]);
    setSelectedPositions([]);
    setOverrideMode(OVERRIDE_KEEP);
    setRotationOverride('');
    setConflict(false);
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    setDeleting(true);
    deletePositionMappingApi(deleteTarget.id).then(() => {
      toast.success('Mapping removed', toastOpts);
      setDeleteModal(false);
      setDeleteTarget(null);
      reload();
    }).catch((e) => {
      toast.error(uniformErrorMessage(e, 'Failed to remove mapping'), toastOpts);
    }).finally(() => setDeleting(false));
  };

  /** Sets rather than `.some()` per row: this runs over every mapping on every
   *  keystroke elsewhere in the component, and a select-all on both pickers
   *  would otherwise make it quadratic. An empty set means "not filtered by
   *  this field", never "match nothing". */
  const itemFilterIds = useMemo(() => new Set(filterItem.map((o) => o.value)), [filterItem]);
  const positionFilterIds = useMemo(() => new Set(filterPosition.map((o) => o.value)), [filterPosition]);

  const filteredRows = useMemo(() => data.filter((row) => {
    if (itemFilterIds.size > 0 && !itemFilterIds.has(rowItemId(row))) return false;
    if (positionFilterIds.size > 0 && !positionFilterIds.has(row.position)) return false;
    return true;
  }), [data, itemFilterIds, positionFilterIds]);

  /** Collapses the flat pair list into one row per position (or per uniform
   *  item), so 48 mappings read as 6 kits rather than 48 near-identical lines.
   *  Each group carries its members' names so the group row alone answers
   *  "what does this position get?" without expanding. */
  const groups = useMemo(() => {
    const byPosition = groupBy === 'position';
    const map = new Map();
    filteredRows.forEach((row) => {
      const key = byPosition ? row.position : rowItemId(row);
      const name = (byPosition ? row.position_name : row.uniform_item?.name) || '—';
      const memberName = (byPosition ? row.uniform_item?.name : row.position_name) || '—';
      // When grouping BY uniform item the item is the group row, so that is
      // where its inactive flag has to render; when grouping by position the
      // item is a member and the flag renders on the detail row instead. Either
      // way a de-activated item stays visible and flagged.
      if (!map.has(key)) {
        map.set(key, {
          key: `${groupBy}-${key}`,
          name,
          inactive: !byPosition && row.uniform_item?.status === false,
          rows: [],
        });
      }
      map.get(key).rows.push({ ...row, memberName });
    });
    return Array.from(map.values())
      .map((group) => ({
        ...group,
        rows: group.rows.sort((a, b) => a.memberName.localeCompare(b.memberName, undefined, { sensitivity: 'base' })),
      }))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }, [filteredRows, groupBy]);

  const groupedByPosition = groupBy === 'position';
  const isFiltered = itemFilterIds.size > 0 || positionFilterIds.size > 0;

  // Flipping the grouping replaces every row with a row that means something
  // else, so carrying the open set across would leave arbitrary-looking rows
  // expanded. Keys are namespaced (`position-3` / `item-3`) so they could never
  // collide, but collapsing is the honest reset.
  useEffect(() => {
    setExpandedKeys([]);
  }, [groupBy]);

  /** Open groups that still exist in the current view. Counted against `groups`
   *  rather than trusting `expandedKeys.length`, because narrowing the filters
   *  can leave keys in state whose groups are no longer rendered — without this
   *  the buttons would disable themselves over rows nobody can see. */
  const expandedVisibleCount = useMemo(() => {
    if (expandedKeys.length === 0) return 0;
    const open = new Set(expandedKeys);
    return groups.reduce((count, group) => (open.has(group.key) ? count + 1 : count), 0);
  }, [expandedKeys, groups]);

  const pinLeft = useStickyIdentity();

  const groupColumns = [
    {
      // Pinned. The expand column antd inserts to the left of this one
      // copies `fixed` off it (rc-table `useColumns`), so the toggle
      // pins with the name instead of stranding it off-screen.
      title: groupedByPosition ? 'Position' : 'Uniform Item',
      dataIndex: 'name',
      ...pinLeft(240),
      render: (name, group) => (
        <span style={{ fontWeight: 600 }}>
          {name}
          {group.inactive && <Tag className="ms-2" color="default">Inactive</Tag>}
        </span>
      ),
    },
    {
      title: groupedByPosition ? 'Uniform Items' : 'Positions',
      render: (_, group) => {
        const shown = group.rows.slice(0, INLINE_TAG_LIMIT);
        const hidden = group.rows.slice(INLINE_TAG_LIMIT);
        return (
          <div className="d-flex flex-wrap gap-1">
            {shown.map((row) => <Tag key={row.id}>{row.memberName}</Tag>)}
            {hidden.length > 0 && (
              <Tooltip title={hidden.map((row) => row.memberName).join(', ')}>
                <Tag>+{hidden.length}</Tag>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: 'Count',
      width: 90,
      align: 'right',
      render: (_, group) => group.rows.length,
    },
  ];

  const renderGroupDetail = (group) => {
    const detailColumns = [
      {
        title: groupedByPosition ? 'Uniform Item' : 'Position',
        dataIndex: 'memberName',
        render: (memberName, row) => (
          <span>
            {memberName}
            {groupedByPosition && row.uniform_item?.status === false && (
              <Tag className="ms-2" color="default">Inactive</Tag>
            )}
          </span>
        ),
      },
      {
        title: 'Rotation (months)',
        width: 190,
        render: (_, row) => {
          const effective = row.effective_rotation_cycle_months;
          const override = row.rotation_cycle_months_override;
          if (!effective) return <span className="text-muted">—</span>;
          return override
            ? <span>{effective} <small className="text-warning">(override)</small></span>
            : <span>{effective}</span>;
        },
      },
      {
        title: 'Action',
        width: 90,
        align: 'right',
        render: (_, row) => (
          <Button
            icon="fa fa-trash"
            className="btn btn-sm btn-outline-danger"
            title={`Remove ${row.uniform_item?.name || 'item'} from ${row.position_name || 'position'}`}
            onClick={() => { setDeleteTarget(row); setDeleteModal(true); }}
          />
        ),
      },
    ];
    return (
      <Table
        columns={detailColumns}
        dataSource={group.rows}
        rowKey={(row) => row.id}
        size="small"
        pagination={false}
      />
    );
  };

  return (
    <div>
      {modalContextHolder}
      <PageHeader
        variant="section"
        title="Position Mappings"
        actions={
          <span className="text-muted">
            {pluralise(data.length, 'mapping')} across {pluralise(new Set(data.map((r) => r.position)).size, 'position')}
          </span>
        }
      />

      <MasterDataRetryAlert
        show={positionError && !positionLoading}
        onRetry={() => dispatch(fetchPosition())}
      >
        Positions could not be loaded, so the position pickers below are empty and no assignment
        can be made.
      </MasterDataRetryAlert>

      <div className="card mb-4">
        <div className="card-body">
          <div className="mb-3">
            <h5 className="mb-1">Assign uniforms to positions</h5>
            <p className="text-muted mb-0">
              Pick any number of uniform items and any number of positions — every combination is written.
              Pairs that already exist are updated, not duplicated, so it is always safe to run this again.
            </p>
          </div>

          {/* Stacked, full width: each picker now holds a whole row. Ticking
              several options at once needs the menu open over a wide control, and
              two half-width selects side by side gave both the narrowest possible
              menu and the most cramped chip row. */}
          <div>
            <PickerHeader
              inputId="pm-assign-items"
              label="Uniform items"
              selectedCount={selectedItems.length}
              totalCount={uniformItems.length}
              loading={itemsLoading}
            />
            <FilterSelect
              inputId="pm-assign-items"
              isMulti={true}
              isClearable={true}
              checkboxMenu={true}
              selectAll={true}
              maxVisibleValues={PICKER_MAX_CHIPS}
              onChange={(value) => setSelectedItems(value || [])}
              optionLabel="label"
              optionValue="value"
              options={uniformItems}
              isLoading={itemsLoading}
              isDisabled={saving}
              value={selectedItems}
              placeholder="Select uniform items"
              noOptionsLabel="uniform items"
            />
          </div>

          {/* The × column is gone with the side-by-side layout, so this carries
              the cross-product idea between the two rows. aria-hidden: the intro
              paragraph above states it in full, and the preview strip below states
              it numerically, so announcing this fragment a third time would only
              add noise. */}
          <div
            className="d-flex align-items-center gap-2 my-2 text-muted small"
            aria-hidden="true"
          >
            <span style={{ fontSize: 15, lineHeight: 1, fontWeight: 300 }}>&times;</span>
            <span>paired with every position below</span>
          </div>

          <div>
            <PickerHeader
              inputId="pm-assign-positions"
              label="Positions"
              selectedCount={selectedPositions.length}
              totalCount={positionOptions.length}
              loading={positionLoading}
            />
            <FilterSelect
              inputId="pm-assign-positions"
              isMulti={true}
              isClearable={true}
              checkboxMenu={true}
              selectAll={true}
              maxVisibleValues={PICKER_MAX_CHIPS}
              onChange={(value) => setSelectedPositions(value || [])}
              optionLabel="label"
              optionValue="value"
              options={positionOptions}
              isLoading={positionLoading}
              isDisabled={saving}
              value={selectedPositions}
              placeholder="Select positions"
              noOptionsLabel="positions"
            />
          </div>

          <div
            className="d-flex flex-wrap align-items-center gap-3 mt-3 pt-3"
            style={{ borderTop: '1px dashed #E8E8E8' }}
          >
            <span style={{ fontWeight: 600 }}>Rotation override</span>
            <Radio.Group
              value={overrideMode}
              onChange={(e) => setOverrideMode(e.target.value)}
              optionType="button"
              buttonStyle="solid"
              disabled={saving}
              aria-label="Rotation override mode"
            >
              <Radio.Button value={OVERRIDE_KEEP}>Leave unchanged</Radio.Button>
              <Radio.Button value={OVERRIDE_SET}>Set to…</Radio.Button>
              <Radio.Button value={OVERRIDE_CLEAR}>Clear</Radio.Button>
            </Radio.Group>
            {overrideMode === OVERRIDE_SET && (
              <div className="d-flex align-items-center gap-2">
                <input
                  className={`form-control ${overrideInvalid ? 'is-invalid' : ''}`}
                  type="number"
                  min="1"
                  style={{ width: 120 }}
                  value={rotationOverride}
                  onChange={(e) => setRotationOverride(e.target.value)}
                  step="1"
                  placeholder="e.g. 12"
                  aria-label="Rotation override in months"
                />
                <span className="text-muted">months</span>
                {overrideInvalid && (
                  <span className="text-danger small">Enter a whole number of months, 1 or more.</span>
                )}
              </div>
            )}
            {overrideMode === OVERRIDE_KEEP && (
              <span className="text-muted small">
                New mappings use the uniform item&apos;s own rotation cycle. Overrides already set on
                existing mappings are left untouched.
              </span>
            )}
            {overrideMode === OVERRIDE_CLEAR && (
              <span className="text-muted small">
                Every mapping in this assignment falls back to its uniform item&apos;s rotation cycle.
              </span>
            )}
          </div>

          <div
            className="mt-3 p-3"
            style={{ background: '#FAFAFA', border: '1px solid #E8E8E8', borderRadius: 12 }}
          >
            {preview.total === 0 ? (
              <span className="text-muted">
                Choose at least one uniform item and one position to see exactly what will be written.
              </span>
            ) : (
              <div className="d-flex justify-content-between align-items-end flex-wrap gap-3">
                <div>
                  <div className="d-flex align-items-baseline flex-wrap gap-2">
                    <span style={{ fontSize: 22, fontWeight: 700, lineHeight: 1 }}>{preview.total}</span>
                    <span className="text-muted">
                      mapping{preview.total === 1 ? '' : 's'} — {pluralise(selectedItems.length, 'uniform item')}
                      {' '}&times; {pluralise(selectedPositions.length, 'position')}
                    </span>
                  </div>
                  <div className="d-flex flex-wrap gap-2 mt-2">
                    <StatPill tone="new">{preview.fresh} new</StatPill>
                    <StatPill tone="existing">{preview.existing} already mapped</StatPill>
                  </div>
                  {overrideChangeCount > 0 && (
                    <div className="text-warning small mt-2">
                      <i className="fa fa-exclamation-triangle me-1" />
                      {overrideMode === OVERRIDE_SET
                        ? `${pluralise(overrideChangeCount, 'existing mapping')} will change to a rotation of ${overrideValue} months.`
                        : `${pluralise(overrideChangeCount, 'existing mapping')} will lose the rotation override and fall back to the uniform item default.`}
                    </div>
                  )}
                  {overLimit && (
                    <div className="text-danger small mt-2">
                      <i className="fa fa-ban me-1" aria-hidden="true" />
                      Over the {MAX_BULK_PAIRS.toLocaleString()}-mapping limit for a single
                      assignment. Narrow the selection and run it in batches.
                    </div>
                  )}
                  {!overLimit && preview.total > BULK_CONFIRM_THRESHOLD && (
                    <div className="text-muted small mt-2">
                      That is a large assignment — you will be asked to confirm.
                    </div>
                  )}
                  {conflict && (
                    <div className="text-warning small mt-2" role="status">
                      <i className="fa fa-refresh me-1" aria-hidden="true" />
                      <strong>Nothing was written.</strong> Another assignment was saving at the same
                      time and kept winning. Your selection is untouched — press Assign to try again.
                    </div>
                  )}
                </div>
                <div className="d-flex align-items-center gap-2">
                  <Button
                    label="Clear"
                    className="btn btn-outline-secondary"
                    disabled={saving}
                    onClick={handleClearSelection}
                  />
                  <Button
                    label={`Assign ${pluralise(preview.total, 'mapping')}`}
                    icon="fa fa-check"
                    className="btn btn-primary"
                    loading={saving}
                    disabled={!canAssign}
                    onClick={handleAssign}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="d-flex justify-content-between align-items-end flex-wrap gap-3 mb-3">
        <div>
          <span className="col-form-label pt-0 d-block">Group by</span>
          <div>
            <Radio.Group
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              optionType="button"
              buttonStyle="solid"
              aria-label="Group mappings by"
            >
              <Radio.Button value="position">Position</Radio.Button>
              <Radio.Button value="item">Uniform item</Radio.Button>
            </Radio.Group>
          </div>
        </div>
        <div className="d-flex align-items-end flex-wrap gap-2">
          <div style={{ minWidth: 220 }}>
            <label className="col-form-label pt-0" htmlFor="pm-filter-item">Filter by item</label>
            <FilterSelect
              inputId="pm-filter-item"
              isMulti
              checkboxMenu
              selectAll
              maxVisibleValues={2}
              isClearable={true}
              onChange={(next) => setFilterItem(next || [])}
              optionLabel="label"
              optionValue="value"
              options={uniformItems}
              isLoading={itemsLoading}
              value={filterItem}
              placeholder="All uniform items"
              noOptionsLabel="uniform items"
            />
          </div>
          <div style={{ minWidth: 220 }}>
            <label className="col-form-label pt-0" htmlFor="pm-filter-position">Filter by position</label>
            <FilterSelect
              inputId="pm-filter-position"
              isMulti
              checkboxMenu
              selectAll
              maxVisibleValues={2}
              isClearable={true}
              onChange={(next) => setFilterPosition(next || [])}
              optionLabel="label"
              optionValue="value"
              options={positionOptions}
              isLoading={positionLoading}
              value={filterPosition}
              placeholder="All positions"
              noOptionsLabel="positions"
            />
          </div>
        </div>
      </div>

      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
        <div className="text-muted">
          {isFiltered && (
            <>
              Showing {filteredRows.length} of {pluralise(data.length, 'mapping')}
              {' · '}
              <button
                type="button"
                className="btn btn-link btn-sm p-0 align-baseline"
                onClick={() => { setFilterItem([]); setFilterPosition([]); }}
              >
                Clear filters
              </button>
            </>
          )}
        </div>
        <div className="d-flex align-items-center gap-2">
          <Button
            label="Expand all"
            icon="fa fa-angle-double-down"
            className="btn btn-sm btn-outline-secondary"
            disabled={groups.length === 0 || expandedVisibleCount === groups.length}
            onClick={() => setExpandedKeys(groups.map((group) => group.key))}
          />
          <Button
            label="Collapse all"
            icon="fa fa-angle-double-up"
            className="btn btn-sm btn-outline-secondary"
            disabled={expandedVisibleCount === 0}
            onClick={() => setExpandedKeys([])}
          />
        </div>
      </div>

      <Table
        columns={groupColumns}
        dataSource={groups}
        rowKey={(group) => group.key}
        loading={loading}
        expandable={{
          expandedRowRender: renderGroupDetail,
          expandedRowKeys: expandedKeys,
          onExpandedRowsChange: (keys) => setExpandedKeys(Array.from(keys)),
        }}
        locale={{
          emptyText: isFiltered
            ? 'No mappings match these filters.'
            : 'No position mappings yet — assign uniform items to positions above.',
        }}
        pagination={{
          total: groups.length,
          defaultPageSize: 50,
          showSizeChanger: true,
          pageSizeOptions,
          itemRender,
        }}
        scroll={{ x: 'max-content' }}
      />

      <DeleteModal
        Name="Remove Position Mapping"
        itemName={
          deleteTarget
            ? `${deleteTarget.uniform_item?.name || 'Item'} → ${deleteTarget.position_name || 'Position'}`
            : undefined
        }
        deleteModal={deleteModal}
        setDeleteModal={setDeleteModal}
        deleteHandler={handleDelete}
        loading={deleting}
      />
    </div>
  );
};

export default PositionMappingList;
